import { $ as $E, type Html, html, Page, View } from 'rune-ts';
import klass from './PopupStoreDetail.module.scss';
import { LayoutView } from '../../components/cells/Layout/LayoutView';
import { footer_links } from '../../components/cells/Footer/constant';
import { UtilImageS } from '../../../../../modules/Util/Image/S/Function/module/UtilImageS';
import { PopupStoreDetailType } from '../../../features/PopupStores/type';
import { staticTypo, staticTypoPcMo, typo } from '../../../shared/typography/typo';
import { ProductCard, ProductLikeToggleEvent } from '../../components/cells/ProductCard/ProductCard';
import { Breadcrumbs } from '../../components/cells/BreadCrumbs/Breadcrumbs';
import { doIf, htmlIf } from '../../../shared/util';
import { BaseLayoutOptions } from '../../../features/Core/service';
import { format } from 'date-fns';
import { hydrateFeed } from '../CurationDetail/util';
import sanitizer from 'sanitizer';
import { ProductCardSize } from '@services/MarppleShop/features/ProductList/type';

export type PopupStoreDetailPageData = {
  user_id?: number;
  popup_store: PopupStoreDetailType;
  layout_options: BaseLayoutOptions;
};

class PopupStoreDetailBody extends View<PopupStoreDetailPageData> {
  br(text: string) {
    return html.preventEscape(text.replaceAll('\n', '<br/>'));
  }

  breadCrumbs: Breadcrumbs;

  constructor(props: PopupStoreDetailPageData) {
    super(props);
    const item = props.popup_store;

    this.breadCrumbs = new Breadcrumbs({
      steps: [
        {
          href: `/${ET.lang}/@/popup-store`,
          label: ET('mps2::section_title::section_tabs::popup_store'),
        },
        {
          href: `/${ET.lang}/@/popup-store/${item.id}`,
          label: `#${props.popup_store.numbering} ${props.popup_store.creator_name}`,
        },
      ],
      color: 'white',
    });
  }

  override template() {
    const {
      user_id,
      popup_store: item,
      layout_options: { is_mobile },
    } = this.data;
    const title_font = is_mobile ? typo('28_bold') : typo('48_bold');
    const description_font = is_mobile ? typo('14_medium') : typo('24_bold');
    const related_title_font = is_mobile ? staticTypo('bebas_40_bold') : staticTypo('bebas_40_bold');
    const related_description_font = is_mobile ? typo('16_bold') : typo('24_bold');
    const related_products = item.related_products?.products || [];

    return html`
      <div class="${klass.popup_store_detail}">
        <div class="${klass.cover_image}">
          <div class="${klass.breadcrumbs}">${htmlIf(this.breadCrumbs, !is_mobile)}</div>
          <img
            src="${UtilImageS.getResizedUrl({
              url: item.cover_thumbnail,
              width: 1920 * 2,
              format: 'jpeg',
              quality: 85,
            })}"
          />
        </div>

        <div class="${klass.summary}">
          <div>
            <h2 class="${klass.title} ${title_font}">${this.br(item.content_title)}</h2>
            <p
              class="${klass.event_date} ${staticTypoPcMo({
                is_mobile,
                pc: 'unica_16_medium',
                mo: 'unica_14_medium',
              })}"
            >
              ${format(new Date(item.start_at), 'yyyy.MM.dd HH:mm')} ~
              ${format(new Date(item.end_at), 'yyyy.MM.dd HH:mm')}
            </p>
          </div>
          <p class="${klass.description} ${description_font}">${this.br(item.content_description)}</p>
        </div>

        <div class="${klass.content}">${html.preventEscape(this._deleteEmptyCaption(item.content))}</div>

        ${doIf(
          () => html`
            <div class="${klass.related_products}">
              <div>
                <h3 class="${klass.related_title} ${related_title_font}">
                  ${this.br(item.product_section_title)}
                </h3>
                <p class="${klass.related_description} ${related_description_font}">
                  ${this.br(item.product_section_description)}
                </p>
              </div>
              <div class="${klass.product_list}">
                ${related_products.map(
                  (product) =>
                    new ProductCard(
                      { ...product, user_id },
                      {
                        resize_width: 260 * 2,
                        size: ProductCardSize.medium,
                      },
                    ),
                )}
              </div>
            </div>
          `,
          related_products.length > 0,
        )}
      </div>
    `;
  }

  override onMount() {
    hydrateFeed(this.element());
    this._removeEmptyPreviews();

    this.delegate(ProductLikeToggleEvent, ProductCard, async (_, view) => {
      await view.toggleLikeButton();
    });
  }

  private _deleteEmptyCaption(text: string) {
    return text.replaceAll('<span class="caption">이미지의 출처를 입력해 주세요.</span>', '');
  }

  private _removeEmptyPreviews() {
    $E(this.element())
      .findAll(`.${klass.content} .FeedProductListPreviewView`)
      .forEach(($element) => {
        if (!$element.find(`.swiper-slide`)) {
          $element.remove();
        }
      });
  }
}

export class PopupStoreDetailPage extends Page<PopupStoreDetailPageData> {
  protected override template(data: PopupStoreDetailPageData): Html {
    const { layout_options } = data;

    return html`
      <div class="${klass.popup_store_detail_page}">
        ${new LayoutView(
          {
            body: new PopupStoreDetailBody(this.data),
            footer_data: { ...footer_links, lang: ET.lang, is_mobile: layout_options.is_mobile },
          },
          {
            full_width: true,
            floating_box: {
              share: {
                title: sanitizer.unescapeEntities(this.data.popup_store.title),
                description: this.data.popup_store.description,
              },
            },
            ...layout_options,
          },
        )}
      </div>
    `;
  }
}
